import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { INSTRUCTORS } from '@/lib/constants';
import SEO from '@/components/SEO';
import { ShieldCheck, Award, Target, Heart } from 'lucide-react';

const FadeInWhenVisible = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export default function Instructors() {
  return (
    <div className="bg-brand-black min-h-screen">
      <SEO
        title="Jiu-Jitsu Instructors & Coaching Team | Delta BJJ"
        description="Looking for a Jiu-Jitsu academy in Culver City? Experience elite training from a top Jiu-Jitsu instructor in a safe, community-focused environment for all ages."
        url="https://www.deltabjj.com/jiu-jitsu-instructors"
      />

      {/* Hero Header */}
      <section className="relative pt-48 pb-20 px-6 lg:px-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <FadeInWhenVisible>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-display font-bold text-white tracking-tighter leading-tight mb-8 break-words">
              Delta BJJ <span className="blue-gradient-text">Instructors</span> <br />
              <span className="text-white">World-Class Brazilian Jiu-Jitsu</span>
            </h1>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-12 md:py-16 px-6 lg:px-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <FadeInWhenVisible>
            <div className="max-w-4xl mx-auto space-y-4 text-center">
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                At Delta BJJ, the quality of your training is defined by the quality of your leadership. To reach your full potential on the mats, you need a dedicated Jiu-Jitsu instructor who understands that the art is as much about mental discipline and community as it is about physical technique.
              </p>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                Our coaching staff is led by world-renowned 6th Degree Black Belt, Professor Gerson Sanginitto. With over 30 years of experience, Professor Gerson has dedicated his life to preserving the authentic culture of the sport while building a modern, inclusive hub for the Culver City community.
              </p>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed font-semibold">
                Whether you are a hobbyist looking for fitness or a competitor looking for an edge, every Jiu-Jitsu instructor at Delta BJJ is committed to:
              </p>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Commitments Section */}
      <section className="py-12 md:py-16 px-6 lg:px-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Target,
                title: "Technical Precision",
                description: "Ensuring every student masters the leverage and mechanics of the R1 Global system."
              },
              {
                icon: ShieldCheck,
                title: "Safety & Respect",
                description: "Maintaining an ego-free environment where students of all ages can train safely."
              },
              {
                icon: Heart,
                title: "Personal Growth",
                description: "Using the mats as a tool to build confidence, resilience, and discipline that carry over into your daily life."
              }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <FadeInWhenVisible key={i} delay={i * 0.1}>
                  <div className="bg-white p-5 rounded-sm border border-gray-100 shadow-sm h-full">
                    <div className="w-12 h-12 bg-brand-blue rounded-lg flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-brand-black mb-2 tracking-tighter">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {item.description}
                    </p>
                  </div>
                </FadeInWhenVisible>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ready to Train CTA */}
      <section className="py-12 md:py-16 px-6 lg:px-12 bg-brand-black text-white border-b border-white/10 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-blue/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <FadeInWhenVisible>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-bold text-white mb-6 tracking-tighter">
              Ready to <span className="blue-gradient-text">Train?</span>
            </h2>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 max-w-4xl mx-auto">
              Meet the team that makes Delta BJJ the premier destination for authentic Brazilian Jiu-Jitsu in Los Angeles. Whether you are looking for world-class competition training or a safe place for your family to learn, our doors are open.
            </p>
            <Link 
              to="/contact-bjj-culver-city" 
              className="btn-premium-outline inline-flex items-center gap-3"
            >
              Contact us today for a Free Trial Lesson
            </Link>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Instructors Grid - High-End Profile Cards */}
      <section className="py-20 md:py-28 px-6 lg:px-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <FadeInWhenVisible>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-bold text-brand-black mb-12 tracking-tighter text-center">
              Meet Our <span className="text-gray-300">Instructors & Coaches</span>
            </h2>
          </FadeInWhenVisible>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
            {INSTRUCTORS.map((instructor, i) => (
              <FadeInWhenVisible key={instructor.id} delay={i * 0.1}>
                <div className="group relative">
                  {/* Image Container with Border Effect */}
                  <div className="relative aspect-[3/4] overflow-hidden rounded-sm mb-8 bg-gray-100 shadow-xl">
                    <img
                      src={instructor.image || '/images/instructor-placeholder.jpg'}
                      alt={instructor.alt || instructor.name}
                      className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 border-[1px] border-black/5 pointer-events-none" />
                  </div>

                  {/* Instructor Info - Rank Badge moved here from overlay */}
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-brand-blue font-display text-[10px] tracking-[0.3em] uppercase block font-bold">
                        {instructor.title}
                      </span>
                      <span className="px-3 py-1.5 bg-brand-black text-white text-[10px] font-bold tracking-[0.2em] uppercase rounded-sm flex items-center gap-2">
                        <Award className="w-3 h-3 text-brand-blue" />
                        {instructor.rank || 'Black Belt'}
                      </span>
                    </div>
                    
                    <h3 className="text-4xl font-display font-bold text-brand-black tracking-tighter mb-6 group-hover:text-brand-blue transition-colors">
                      {instructor.name}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed font-light mb-8 max-w-xs">
                      {instructor.bio}
                    </p>
                    
                    {/* Decorative element */}
                    <div className="w-12 h-[1px] bg-gray-200 group-hover:w-24 group-hover:bg-brand-blue transition-all duration-500" />
                  </div>
                </div>
              </FadeInWhenVisible>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
